import React, { useState, useMemo, useEffect } from 'react';
import { useProperties } from '../../hooks/useProperties';
import { PropertyCard } from '../Properties/PropertyCard';
import { PropertyDetailsModal } from '../PropertyDetails/PropertyDetailsModal';
import { PropertySearchFilters } from '../../types/Property';
import './HomePage.css';
import familyBackground from '../../assets/family-background.png';

interface HomePageProps {
  onSearch: (searchData: any) => void;
  onNavigate?: (page: string) => void;
}

export const HomePage: React.FC<HomePageProps> = ({ onSearch, onNavigate }) => {
  const [searchData, setSearchData] = useState({
    estado: 'SC',
    cidade: 'Todas',
    bairro: '',
    tipoImovel: '',
    valorMaximo: '',
    quartos: '',
    finalidade: '',
  });
  const [selectedPropertyData, setSelectedPropertyData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Buscar imóveis sem filtro de cidade (Todas)
  const [defaultFilters] = useState<PropertySearchFilters>({
    bairro: '',
    estado: 'SC',
    page: 1,
    limit: 100, // Buscar mais imóveis para poder ordenar e pegar os 3 com maior valor
  });

  const { imoveis: allImoveis } = useProperties(defaultFilters);

  // Ordenar imóveis por valor (aluguel ou venda) decrescente e pegar os 3 primeiros
  const imoveis = useMemo(() => {
    const sorted = [...allImoveis].sort((a, b) => {
      // Calcular valor principal para ordenação (priorizar venda se existir, senão aluguel)
      const valorA = a.valor_venda && a.valor_venda > 0 ? a.valor_venda : (a.valor_aluguel || 0);
      const valorB = b.valor_venda && b.valor_venda > 0 ? b.valor_venda : (b.valor_aluguel || 0);
      return valorB - valorA;
    });
    // Pegar apenas os 3 primeiros (maior valor)
    return sorted.slice(0, 3);
  }, [allImoveis]);

  const handleInputChange = (field: string, value: string) => {
    setSearchData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    // Preparar dados de busca incluindo tipo_imovel
    const searchParams: any = {
      estado: searchData.estado || 'SC',
    };
    
    // Só adicionar cidade se não for "Todas"
    if (searchData.cidade && searchData.cidade !== 'Todas') {
      searchParams.cidade = searchData.cidade;
    }

    // Adicionar filtros opcionais apenas se tiverem valores
    if (searchData.bairro && searchData.bairro.trim() !== '') {
      searchParams.bairro = searchData.bairro.trim();
    }

    if (searchData.tipoImovel && searchData.tipoImovel.trim() !== '') {
      searchParams.tipoImovel = searchData.tipoImovel.trim();
    }

    if (searchData.valorMaximo && searchData.valorMaximo.trim() !== '') {
      searchParams.valorMaximo = searchData.valorMaximo.trim();
    }

    if (searchData.quartos && searchData.quartos.trim() !== '') {
      searchParams.quartos = searchData.quartos.trim();
    }

    if (searchData.finalidade && searchData.finalidade.trim() !== '') {
      const finalidade = searchData.finalidade.trim();
      searchParams.finalidade = finalidade; // Manter o valor original para a SearchPage
      if (finalidade === 'aluguel') {
        searchParams.apenas_aluguel = true;
      } else if (finalidade === 'venda') {
        searchParams.apenas_venda = true;
      } else if (finalidade === 'ambos') {
        searchParams.aluguel_e_venda = true;
      }
    }

    // Verificar se há filtros além da cidade/estado padrão
    const hasAdditionalFilters = searchParams.bairro || searchParams.tipoImovel || 
                                   searchParams.valorMaximo || searchParams.quartos || searchParams.apenas_aluguel || searchParams.apenas_venda || searchParams.aluguel_e_venda;
    
    if (!hasAdditionalFilters) {
      // Se não há filtros adicionais, ainda assim enviar os parâmetros padrão
      // para buscar em Balneário Camboriú
      onSearch(searchParams);
    } else {
      onSearch(searchParams);
    }
  };

  const handlePropertyClick = (imovel: any) => {
    console.log('Imóvel clicado:', imovel);
    setSelectedPropertyData(imovel);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPropertyData(null);
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    
    const checkMobile = () => {
      const mobile = mediaQuery.matches;
      setIsMobile(mobile);
    };

    checkMobile();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', checkMobile);
    } else {
      mediaQuery.addListener(checkMobile);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', checkMobile);
      } else {
        mediaQuery.removeListener(checkMobile);
      }
    };
  }, []);


  return (
    <div className="home-page">
      {/* Imagem de fundo */}
      <div 
        className="background-image"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          minHeight: '100vh',
          backgroundImage: `url(${isMobile ? '/banner.png' : familyBackground})`,
          backgroundSize: isMobile ? 'cover' : '100% auto',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: isMobile ? 'scroll' : 'fixed',
          zIndex: 1
        }}
      />
      
      {/* Overlay claro para melhor contraste */}
      <div 
        className="background-overlay"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.35))',
          zIndex: 2
        }}
      />
      
      <div className="home-container">
        <div className="home-content-wrapper">
          {/* Painel de Busca */}
          <div className="search-panel">
            <h1 className="search-title">O imóvel perfeito não se procura, encontra-se aqui!</h1>

            <div className="search-form">
              {/* Linha 1: Cidade, Tipo de Imóvel e Bairro */}
              <div className="search-row">
                <div className="search-field">
                  <select
                    value={searchData.cidade}
                    onChange={(e) => handleInputChange('cidade', e.target.value)}
                    className="search-select"
                  >
                    <option value="Todas">Todas</option>
                    <option value="Balneário Camboriú">Balneário Camboriú</option>
                    <option value="Itajaí">Itajaí</option>
                  </select>
                </div>

                <div className="search-field">
                  <select
                    value={searchData.tipoImovel}
                    onChange={(e) => handleInputChange('tipoImovel', e.target.value)}
                    className="search-select"
                  >
                    <option value="">Tipo de imóvel</option>
                    <option value="apartamento">Apartamento</option>
                    <option value="casa">Casa</option>
                    <option value="kitnet">Kitnet</option>
                    <option value="studio">Studio</option>
                    <option value="loft">Loft</option>
                    <option value="duplex">Duplex</option>
                    <option value="cobertura">Cobertura</option>
                    <option value="terreno">Terreno</option>
                    <option value="comercial">Comercial</option>
                    <option value="galpao">Galpão</option>
                  </select>
                </div>

                <div className="search-field">
                  <input
                    type="text"
                    placeholder="Centro, Praia dos Amores, Barra Sul..."
                    value={searchData.bairro}
                    onChange={(e) => handleInputChange('bairro', e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* Linha 2: Finalidade, Valor e Quartos */}
              <div className="search-row">
                <div className="search-field">
                  <select
                    value={searchData.finalidade}
                    onChange={(e) => handleInputChange('finalidade', e.target.value)}
                    className="search-select"
                  >
                    <option value="">Finalidade</option>
                    <option value="aluguel">Aluguel</option>
                    <option value="venda">Venda</option>
                    <option value="ambos">Aluguel e Venda</option>
                  </select>
                </div>

                <div className="search-field">
                  <select
                    value={searchData.valorMaximo}
                    onChange={(e) => handleInputChange('valorMaximo', e.target.value)}
                    className="search-select"
                  >
                    <option value="">Escolha o valor</option>
                    <option value="1000">Até R$ 1.000</option>
                    <option value="2000">Até R$ 2.000</option>
                    <option value="3000">Até R$ 3.000</option>
                    <option value="5000">Até R$ 5.000</option>
                    <option value="10000">Até R$ 10.000</option>
                    <option value="15000">Até R$ 15.000</option>
                    <option value="20000">Até R$ 20.000</option>
                    <option value="30000">Até R$ 30.000</option>
                    <option value="50000">Até R$ 50.000</option>
                  </select>
                </div>

                <div className="search-field">
                  <select
                    value={searchData.quartos}
                    onChange={(e) => handleInputChange('quartos', e.target.value)}
                    className="search-select"
                  >
                    <option value="">N° de quartos</option>
                    <option value="1">1 quarto</option>
                    <option value="2">2 quartos</option>
                    <option value="3">3 quartos</option>
                    <option value="4">4+ quartos</option>
                  </select>
                </div>
              </div>

              {/* Linha 3: Botão Buscar na segunda coluna */}
              <div className="search-row">
                <div className="search-field" style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                  <select className="search-select" disabled>
                    <option value=""></option>
                  </select>
                </div>

                <div className="search-field">
                  <button className="search-button" onClick={handleSearch}>
                    Buscar
                  </button>
                </div>

                <div className="search-field" style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                  <select className="search-select" disabled>
                    <option value=""></option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Seção de Imóveis em Destaque */}
          {imoveis.length > 0 && (
            <div className="home-properties-section">
              <div className="properties-title-wrapper">
                <h2 className="properties-section-title">Imóveis em Destaque</h2>
              </div>
              <div className="home-properties-grid">
                {imoveis.map((imovel) => (
                  <PropertyCard 
                    key={imovel.id} 
                    imovel={imovel}
                    onClick={handlePropertyClick}
                  />
                ))}
              </div>
              
              {/* Botão Carregar Mais */}
              <div className="load-more-wrapper">
                <button className="load-more-button" onClick={() => onNavigate && onNavigate('search')}>
                  Carregar Mais Imóveis
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalhes do imóvel */}
      {selectedPropertyData && (
        <PropertyDetailsModal 
          propertyId={selectedPropertyData.id} 
          propertyData={selectedPropertyData}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
